<template>
  <v-form ref="timelineViewRangeEditorForm">
    <dialog-base
      v-bind:shows="shows"
      v-bind:initialize-callback="$_onInitialize"
      v-bind:ok-callback="$_okCallback"
      v-bind:finalize-callback="finalizeCallback"
      v-bind:ok-disabled="$data.$_hasError"
    >
      <template v-slot:body>
        <v-card-title>Timeline View Range</v-card-title>
        <v-card-text>
          <v-checkbox
            v-if="$_isVideoDurationSet"
            v-bind:label="$_usesVideoDurationCheckboxLabel"
            v-model="$data.$_usesVideoDuration"
          >
          </v-checkbox>
          <general-text-field
            autofocus 
            label="Timeline View Begin"
            suffix="msec"
            type="number"
            v-bind:rules="$_rules.begin"
            v-bind:disabled="$data.$_usesVideoDuration"
            v-model.number="$_timelineViewBeginMinMsec"
            v-on:input="$_validateInput"
          >
          </general-text-field>
          <general-text-field
            label="Timeline View End"
            suffix="msec"
            type="number"
            v-bind:rules="$_rules.end"
            v-bind:disabled="$data.$_usesVideoDuration"
            v-model.number="$_timelineViewEndMaxMsec"
            v-on:input="$_validateInput"
          >
          </general-text-field>
        </v-card-text>
      </template>
    </dialog-base>
  </v-form>
</template>

<script>
import DialogBase from './DialogBase.vue';
import GeneralTextField from '../Parts/Inputs/GeneralTextField.vue';

export default {
  components: {
    DialogBase,
    GeneralTextField,
  },

  props: {
    shows:                       { type: Boolean },
    okCallback:                  { type: Function },
    finalizeCallback:            { type: Function },
    timelineViewDurationMinMsec: { type: Number, default: 1000 },
    timelineViewBeginMinMsec:    { type: Number, default: 0 },
    timelineViewEndMaxMsec:      { type: Number, default: 10000 },
    videoDurationMsec:           { type: Number },
  },

  data() {
    return {
      $_hasError: false,
      $_usesVideoDuration: false,
      $_tempTimelineViewBeginMinMsec: undefined,
      $_tempTimelineViewEndMaxMsec: undefined,
    };
  },

  computed: {
    $_usesVideoDurationCheckboxLabel() {
      return `Use Video Duration(${this.videoDurationMsec} msec)`;
    },

    $_isVideoDurationSet() {
      return (this.videoDurationMsec !== undefined);
    },

    $_timelineViewBeginMinMsec: {
      get() {
        if (this.$data.$_usesVideoDuration) {
          return 0;
        } else {
          return this.$data.$_tempTimelineViewBeginMinMsec;
        }
      },

      set(newTimelineViewBeginMinMsec) {
        this.$data.$_tempTimelineViewBeginMinMsec = newTimelineViewBeginMinMsec;
      },
    },

    $_timelineViewEndMaxMsec: {
      get() {
        if (this.$data.$_usesVideoDuration) {
          return this.videoDurationMsec;
        } else {
          return this.$data.$_tempTimelineViewEndMaxMsec;
        }
      },

      set(newTimelineViewEndMaxMsec) {
        this.$data.$_tempTimelineViewEndMaxMsec = newTimelineViewEndMaxMsec;
      },
    },

    $_rules() {
      return {
        begin: [
          mustBeZeroOrAbove,
          beginTimeMustBeLessThanEndTime.bind(this),
          beginTimeMustBeLessThanEndTimeMinusMinimumDuration.bind(this),
        ],
        end: [
          mustBeZeroOrAbove,
          endTimeMustBeMoreThanBeginTime.bind(this),
          endTimeMustBeMoreThanBeginTimePlusMinimumDuration.bind(this),
        ],
      };

      function mustBeZeroOrAbove(numberOrStringTime) {
        let stringValue = String(numberOrStringTime);
        if (stringValue.length > 0) {
          return (Number(stringValue) >= 0) || 'Must be 0 or above.';
        } else {
          return 'Must not be empty.';
        }
      }

      function eitherBeginTimeOrEndTimeEmpty(beginTime, endTime) {
        return (beginTime === '') || (endTime === '');
      }

      function beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration(beginTime, endTime) {
        return (beginTime <= (endTime - this.timelineViewDurationMinMsec));
      }

      function beginTimeMustBeLessThanEndTime(numberOrStringBeginTime) {
        if (eitherBeginTimeOrEndTimeEmpty(numberOrStringBeginTime, this.$data.$_tempTimelineViewEndMaxMsec)) return true;
        let beginTime = Number(numberOrStringBeginTime);
        let endTime = Number(this.$data.$_tempTimelineViewEndMaxMsec);
        return (beginTime < endTime) || 'Begin time must be less than end time.';
      }

      function beginTimeMustBeLessThanEndTimeMinusMinimumDuration(numberOrStringBeginTime) {
        if (eitherBeginTimeOrEndTimeEmpty(numberOrStringBeginTime, this.$data.$_tempTimelineViewEndMaxMsec)) return true;
        let beginTime = Number(numberOrStringBeginTime);
        let endTime = Number(this.$data.$_tempTimelineViewEndMaxMsec);
        return beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration.call(this, beginTime, endTime) || `Timeline view duration is too short(min: ${this.timelineViewDurationMinMsec} milliseconds)`;
      }

      function endTimeMustBeMoreThanBeginTime(numberOrStringEndTime) {
        if (eitherBeginTimeOrEndTimeEmpty(this.$data.$_tempTimelineViewBeginMinMsec, numberOrStringEndTime)) return true;
        let beginTime = Number(this.$data.$_tempTimelineViewBeginMinMsec);
        let endTime = Number(numberOrStringEndTime);
        return (beginTime < endTime);
      }

      function endTimeMustBeMoreThanBeginTimePlusMinimumDuration(numberOrStringEndTime) {
        if (eitherBeginTimeOrEndTimeEmpty(this.$data.$_tempTimelineViewBeginMinMsec, numberOrStringEndTime)) return true;
        let beginTime = Number(this.$data.$_tempTimelineViewBeginMinMsec);
        let endTime = Number(numberOrStringEndTime);
        return beginTimeMustBeEqualToOrLessThanEndTimeMinusMinimumDuration.call(this, beginTime, endTime);
      }
    },
  },

  watch: {
    timelineViewBeginMinMsec() { this.$_onInitialize() },
    timelineViewEndMaxMsec() { this.$_onInitialize() },
  },

  mounted() {
    this.$nextTick(this.$_validateInput);
  },

  methods: {
    $_onInitialize() {
      this.$data.$_tempTimelineViewBeginMinMsec = this.timelineViewBeginMinMsec;
      this.$data.$_tempTimelineViewEndMaxMsec = this.timelineViewEndMaxMsec;
      if (this.$_isVideoDurationSet) {
        if ((this.timelineViewBeginMinMsec === 0) && (this.timelineViewEndMaxMsec === this.videoDurationMsec)) {
          this.$data.$_usesVideoDuration = true;
        }
      }
    },

    $_okCallback() {
      this.okCallback({
        timelineViewBeginMinMsec: this.$_timelineViewBeginMinMsec,
        timelineViewEndMaxMsec: this.$_timelineViewEndMaxMsec,
      });
    },

    $_validateInput() {
      this.$data.$_hasError = !this.$refs.timelineViewRangeEditorForm.validate();
    },
  },
}
</script>